import { Fragment } from 'react';
import { bool, string, shape, arrayOf, number } from 'prop-types';
import styled from 'styled-components/macro';

import CompareModePropType from 'propTypes/compareMode';

import KeyValuePair from 'components/base/KeyValuePair/KeyValuePair';
import { Group, Data, Header } from 'components/base/KeyValuePair/KeyValuePair';

import modularScale from 'styles/helpers/modular-scale';

export const KeyValueList = styled('dl')`
  display: flex;
  flex-flow: row wrap;
  margin: ${({ theme }) => `-${theme.spacing.xsmall}`};
  flex-direction: ${({ showColumn }) => (showColumn ? 'column' : 'row')};

  ${Group} {
    margin: ${({ theme, newDesign }) =>
      newDesign ? `0.4em ${theme.spacing.xsmall}` : theme.spacing.xsmall};
    &:first-of-type {
      ${({ showRow }) =>
        !showRow &&
        `
        @media screen and (min-width: 50em) {
          flex-basis: 100%;
        }`}

      ${({ newDesign }) => (newDesign ? 'dt{margin-top: 10px;}' : '')}
      ${Data} {
        font-size: ${({ newDesign }) =>
          newDesign ? '24px!important' : `${modularScale(3)}`};
        @media screen and (min-width: 50em) {
          font-size: ${({ compareMode }) => modularScale(compareMode ? 3 : 4)};
        }
      }
    }
    &:last-of-type {
      ${({ newDesign }) => (newDesign ? 'margin-bottom: 10px;' : '')}
      @media screen and (max-width: 50em) {
        margin-top: 35px;
        div > dd {
          line-height: 1.2 !important;
        }
      }
    }
  }
`;

/**
 * KeyValue KPI block. This accepts a default title and value which is shown bigger,
 * and also a previous and change value
 * */
const KeyValueGroup = ({
  dataColors,
  compareMode,
  items,
  className,
  showCyAttr,
  showRow,
  showColumn,
  newDesign,
  isMobileView,
  isFullNumber,
  isKPIValue,
  additionalHeader,
}) => {
  return (
    <KeyValueList
      compareMode={compareMode}
      isMobileView={isMobileView}
      className={className}
      showRow={showRow}
      showColumn={showColumn}
      newDesign={newDesign}
    >
      {items?.map((item, index) => {
        return (
          <Fragment key={`${item.metric?.label}-${item.label}-${index}`}>
            {additionalHeader && index === 1 && (
              <Header
                display="flex"
                marginTop={isMobileView ? '2.5em' : '0.5em'}
                marginBottom={isMobileView ? '-1.5em' : '0.35em'}
              >
                {additionalHeader}
              </Header>
            )}
            <KeyValuePair
              label={item.label}
              value={item.metric?.value}
              type={item?.type || item.metric?.type}
              cy={item.cy}
              dataColor={dataColors}
              showCyAttr={showCyAttr}
              showRow={showRow}
              showColumn={showColumn}
              isMobileView={isMobileView}
              newDesign={newDesign}
              isFullNumber={isFullNumber}
              compareMode={compareMode}
              isKPIValue={isKPIValue}
            />
          </Fragment>
        );
      })}
    </KeyValueList>
  );
};

const valuePropType = shape({
  label: string,
  value: arrayOf(
    shape({
      value: number, //can be null
      type: string.isRequired,
    })
  ).isRequired,
});

KeyValueGroup.defaultProps = {
  items: [],
};

KeyValueGroup.propTypes = {
  dataColors: arrayOf(string),
  compareMode: CompareModePropType,
  items: arrayOf(
    shape({
      label: string,
      metric: valuePropType.isRequired,
      cy: string,
    }).isRequired
  ),
  className: string,
  showCyAttr: bool,
  showRow: bool,
  showColumn: bool,
  newDesign: bool,
  isFullNumber: bool,
  isMobileView: bool,
  isKPIValue: bool,
  additionalHeader: string,
};

export default KeyValueGroup;
